import { useState, useEffect, useCallback } from 'react';
import { Routes, Route, Link, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { 
  Home, Users, Calendar, BookOpen, ShoppingBag, Clock, Mail,
  Plus, Edit, Trash2, Eye, Send, Download, Settings,
  CreditCard, FileText, UserCheck, School, Package, Globe,
  Filter, LayoutGrid, Search, ChevronDown, Baby, RefreshCw, ExternalLink
} from 'lucide-react';
import SchoolRegisterView from '../components/admin/SchoolRegisterView';
import ManualEnrollmentModal from '../components/admin/ManualEnrollmentModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Merged Admin Dashboard - combines stats and families overview
const AdminOverview = () => {
  const navigate = useNavigate();
  const [data, setData] = useState({ families: [], terms: [], schools: [], stats: {} });
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ school: 'all', term: 'all', search: '' });
  const [expandedFamilies, setExpandedFamilies] = useState({});
  const [showManualEnroll, setShowManualEnroll] = useState(false);
  const [classes, setClasses] = useState([]);
  const [showRegister, setShowRegister] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/unified-overview`);
      setData(response.data);
    } catch (error) { 
      console.error('Failed to fetch overview:', error); 
      toast.error('Failed to load data');
    } finally { setLoading(false); }
  }, []);

  const fetchClasses = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/classes`);
      setClasses(response.data.filter(c => c.is_active !== false));
    } catch (error) { console.error('Failed to fetch classes:', error); }
  }, []);

  useEffect(() => { 
    fetchData(); 
    fetchClasses(); 
  }, [fetchData, fetchClasses]);

  const toggleFamily = (parentId) => {
    setExpandedFamilies(prev => ({ ...prev, [parentId]: !prev[parentId] }));
  };

  const handleDeleteFamily = async (parentId, parentName) => {
    if (!window.confirm(`Are you sure you want to delete the entire family account for "${parentName}"?\n\nThis will permanently remove:\n• The parent account\n• All children\n• All enrollment history\n\nThis action cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/admin/families/${parentId}`);
      toast.success(`Family "${parentName}" deleted successfully`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete family');
    }
  };

  const handleDeleteChild = async (childId, childName, e) => {
    e.stopPropagation();
    if (!window.confirm(`Are you sure you want to delete "${childName}"?\n\nThis will also remove all their enrollment history.\n\nThis action cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/admin/children/${childId}`);
      toast.success(`Child "${childName}" deleted successfully`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete child');
    }
  };

  const handleDeleteEnrollment = async (enrollmentId, className, e) => {
    e.stopPropagation();
    if (!window.confirm(`Are you sure you want to remove this enrollment from "${className}"?`)) return;
    try {
      await axios.delete(`${API}/admin/enrollments/${enrollmentId}`);
      toast.success('Enrollment deleted');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete enrollment');
    }
  };

  const filteredFamilies = data.families?.filter(family => {
    if (filters.school !== 'all' && !family.schools.includes(filters.school)) return false;
    if (filters.term !== 'all' && !family.terms.includes(filters.term)) return false;
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      const matchParent = family.parent_name.toLowerCase().includes(searchLower) || family.parent_email.toLowerCase().includes(searchLower);
      const matchChild = family.children.some(c => c.name.toLowerCase().includes(searchLower));
      if (!matchParent && !matchChild) return false;
    }
    return true;
  }) || [];

  // Show School Register View if selected
  if (showRegister) {
    return <SchoolRegisterView onBack={() => setShowRegister(false)} />;
  }

  if (loading) {
    return <div className="flex justify-center py-12"><div className="w-12 h-12 border-4 border-teal-600 border-t-transparent rounded-full animate-spin"></div></div>;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <h1 className="font-heading text-3xl font-bold text-slate-900">Admin Dashboard</h1>
        <div className="flex gap-2">
          <button onClick={() => setShowManualEnroll(true)} className="btn-primary text-sm" data-testid="manual-enroll-btn">
            <Plus className="w-4 h-4 mr-2 inline" />Manual Enrollment
          </button>
          <button onClick={fetchData} className="btn-outline text-sm" data-testid="refresh-overview">
            <RefreshCw className="w-4 h-4 mr-2 inline" />Refresh
          </button>
        </div>
      </div>

      {/* Stats Cards - clickable */}
      <div className="grid md:grid-cols-4 gap-4">
        <div className="card-plain hover:border-teal-500 transition-colors cursor-default" data-testid="stats-families">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-teal-100 rounded-xl flex items-center justify-center"><Users className="w-6 h-6 text-teal-600" /></div>
            <div><p className="font-body text-slate-500 text-sm">Families</p><p className="font-heading text-2xl font-bold text-slate-900">{data.stats?.total_families || 0}</p></div>
          </div>
        </div>
        <button 
          onClick={() => setShowRegister(true)} 
          className="card-plain hover:border-pink-500 transition-colors cursor-pointer text-left" 
          data-testid="stats-children"
        >
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-pink-100 rounded-xl flex items-center justify-center"><UserCheck className="w-6 h-6 text-pink-500" /></div>
            <div><p className="font-body text-slate-500 text-sm">Children</p><p className="font-heading text-2xl font-bold text-slate-900">{data.stats?.total_children || 0}</p></div>
          </div>
          <p className="text-xs text-pink-500 mt-2 font-body">Click to view School Register (enrolled) →</p>
        </button>
        <div className="card-plain hover:border-blue-500 transition-colors cursor-default" data-testid="stats-enrollments">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center"><Calendar className="w-6 h-6 text-blue-600" /></div>
            <div><p className="font-body text-slate-500 text-sm">Enrollments</p><p className="font-heading text-2xl font-bold text-slate-900">{data.stats?.total_enrollments || 0}</p></div>
          </div>
        </div>
        <div className="card-plain hover:border-green-500 transition-colors cursor-default" data-testid="stats-revenue">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center"><CreditCard className="w-6 h-6 text-green-600" /></div>
            <div><p className="font-body text-slate-500 text-sm">Total Paid</p><p className="font-heading text-2xl font-bold text-slate-900">£{(data.stats?.total_paid || 0).toFixed(0)}</p></div>
          </div>
        </div>
      </div>

      {/* Quick Links */}
      <div className="grid md:grid-cols-4 gap-4">
        <Link to="/admin/schools" className="card-plain hover:border-teal-500 transition-colors">
          <div className="flex items-center gap-3">
            <School className="w-5 h-5 text-teal-600" />
            <span className="font-heading font-bold text-slate-900">Manage Schools</span>
          </div>
        </Link>
        <Link to="/admin/classes" className="card-plain hover:border-teal-500 transition-colors">
          <div className="flex items-center gap-3">
            <Calendar className="w-5 h-5 text-teal-600" />
            <span className="font-heading font-bold text-slate-900">Manage Classes</span>
          </div>
        </Link>
        <Link to="/admin/workshops" className="card-plain hover:border-pink-500 transition-colors">
          <div className="flex items-center gap-3">
            <Calendar className="w-5 h-5 text-pink-500" />
            <span className="font-heading font-bold text-slate-900">Workshops</span>
          </div>
        </Link>
        <Link to="/admin/summaries" className="card-plain hover:border-yellow-500 transition-colors">
          <div className="flex items-center gap-3">
            <BookOpen className="w-5 h-5 text-yellow-600" />
            <span className="font-heading font-bold text-slate-900">Weekly Summaries</span>
          </div>
        </Link>
      </div>

      {/* Filters */}
      <div className="card-plain">
        <div className="flex items-center gap-2 mb-4"><Filter className="w-5 h-5 text-slate-500" /><span className="font-heading font-bold text-slate-900">Filter Families</span></div>
        <div className="grid md:grid-cols-3 gap-4">
          <div>
            <label className="block font-body text-sm font-semibold text-slate-700 mb-1">School</label>
            <select value={filters.school} onChange={(e) => setFilters({...filters, school: e.target.value})} className="w-full px-4 py-2 border border-slate-200 rounded-xl font-body" data-testid="filter-school">
              <option value="all">All Schools</option>
              {data.schools?.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div>
            <label className="block font-body text-sm font-semibold text-slate-700 mb-1">Term</label>
            <select value={filters.term} onChange={(e) => setFilters({...filters, term: e.target.value})} className="w-full px-4 py-2 border border-slate-200 rounded-xl font-body" data-testid="filter-term">
              <option value="all">All Terms</option>
              {data.terms?.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <div>
            <label className="block font-body text-sm font-semibold text-slate-700 mb-1">Search</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input type="text" placeholder="Search parent or child..." value={filters.search} onChange={(e) => setFilters({...filters, search: e.target.value})} className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-xl font-body" data-testid="filter-search" />
            </div>
          </div>
        </div>
        {(filters.school !== 'all' || filters.term !== 'all' || filters.search) && (
          <button onClick={() => setFilters({ school: 'all', term: 'all', search: '' })} className="mt-4 text-sm text-teal-600 hover:text-teal-700 font-body font-semibold">Clear all filters</button>
        )}
      </div>

      {/* Families List */}
      <div className="space-y-4">
        <span className="font-body text-slate-600">{filteredFamilies.length} families found</span>
        
        {filteredFamilies.length === 0 ? (
          <div className="card-plain text-center py-12"><Users className="w-16 h-16 text-slate-300 mx-auto mb-4" /><p className="font-body text-slate-600">No families match your filters.</p></div>
        ) : (
          filteredFamilies.map(family => (
            <div key={family.parent_id} className="card-plain" data-testid={`family-${family.parent_id}`}>
              {/* Parent Header */}
              <div className="flex items-center justify-between cursor-pointer" onClick={() => toggleFamily(family.parent_id)}>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center">
                    <Users className="w-6 h-6 text-teal-600" />
                  </div>
                  <div>
                    <h3 className="font-heading text-lg font-bold text-slate-900">{family.parent_name}</h3>
                    <p className="font-body text-slate-500 text-sm">{family.parent_email} {family.parent_phone && `• ${family.parent_phone}`}</p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="text-right">
                    <span className="text-sm font-body text-slate-500">{family.children.length} {family.children.length === 1 ? 'child' : 'children'}</span>
                    {family.total_paid > 0 && <p className="font-heading font-bold text-teal-600">£{family.total_paid.toFixed(0)} paid</p>}
                  </div>
                  <button
                    onClick={(e) => { e.stopPropagation(); handleDeleteFamily(family.parent_id, family.parent_name); }}
                    className="p-2 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                    title="Delete family"
                    data-testid={`delete-family-${family.parent_id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                  <ChevronDown className={`w-5 h-5 text-slate-400 transition-transform ${expandedFamilies[family.parent_id] ? 'rotate-180' : ''}`} />
                </div>
              </div>

              {/* Children (Expanded) */}
              {expandedFamilies[family.parent_id] && (
                <div className="mt-4 pt-4 border-t border-slate-200 space-y-4">
                  {family.children.map(child => (
                    <div key={child.id} className="bg-slate-50 rounded-xl p-4" data-testid={`child-${child.id}`}>
                      <div className="flex items-start justify-between">
                        <div>
                          <h4 className="font-heading font-bold text-slate-900">{child.name}</h4>
                          <p className="font-body text-slate-500 text-sm">Age {child.age} • Year {child.year_group}</p>
                          {child.is_returning && <span className="text-xs bg-yellow-100 text-yellow-700 px-2 py-0.5 rounded">Returning</span>}
                        </div>
                        <div className="flex items-center gap-2">
                          <span className={`text-xs font-bold px-2 py-1 rounded ${child.is_enrolled ? 'bg-teal-100 text-teal-700' : 'bg-slate-200 text-slate-600'}`}>
                            {child.is_enrolled ? 'Enrolled' : 'Not Enrolled'}
                          </span>
                          <button
                            onClick={(e) => handleDeleteChild(child.id, child.name, e)}
                            className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                            title="Delete child"
                            data-testid={`delete-child-${child.id}`}
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </div>
                      {/* Enrollment History */}
                      {child.enrollments && child.enrollments.length > 0 && (
                        <div className="mt-3">
                          <p className="font-body text-xs font-semibold text-slate-500 uppercase mb-2">Enrollment History</p>
                          <div className="space-y-2">
                            {child.enrollments.map(e => (
                              <div key={e.id} className="flex items-center justify-between text-sm bg-white rounded-lg px-3 py-2 border border-slate-100">
                                <div>
                                  <span className="font-body font-semibold text-slate-700">{e.class_name}</span>
                                  <span className="text-slate-400 mx-2">•</span>
                                  <span className="font-body text-slate-500">{e.school_name}</span>
                                </div>
                                <div className="flex items-center gap-2">
                                  <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded">{e.term}</span>
                                  <span className={`text-xs font-bold px-2 py-0.5 rounded ${e.payment_status === 'paid' || e.status === 'confirmed' ? 'bg-teal-100 text-teal-700' : 'bg-yellow-100 text-yellow-700'}`}>
                                    {e.amount > 0 ? `£${e.amount.toFixed(0)}` : 'Free'}
                                  </span>
                                  <button
                                    onClick={(ev) => handleDeleteEnrollment(e.id, e.class_name, ev)}
                                    className="p-1 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors"
                                    title="Delete enrollment"
                                    data-testid={`delete-enrollment-${e.id}`}
                                  >
                                    <Trash2 className="w-3 h-3" />
                                  </button>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {/* Manual Enrollment Modal */}
      <ManualEnrollmentModal 
        isOpen={showManualEnroll} 
        onClose={() => setShowManualEnroll(false)} 
        onSuccess={fetchData}
        classes={classes}
      />
    </div>
  );
};

// Schools Management
const SchoolsPage = () => {
  const [schools, setSchools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingSchool, setEditingSchool] = useState(null);
  const [formData, setFormData] = useState({ name: '', address: '', contact_email: '' });

  const fetchSchools = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/schools`);
      setSchools(response.data);
    } catch (error) { console.error('Failed to fetch schools:', error); } 
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchSchools(); }, [fetchSchools]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingSchool) {
        await axios.put(`${API}/admin/schools/${editingSchool.id}`, formData);
        toast.success('School updated');
      } else {
        await axios.post(`${API}/admin/schools`, formData);
        toast.success('School created');
      }
      fetchSchools();
      resetForm();
    } catch (error) { toast.error('Failed to save school'); }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to deactivate this school?')) {
      try {
        await axios.delete(`${API}/admin/schools/${id}`);
        toast.success('School deactivated');
        fetchSchools();
      } catch (error) { toast.error('Failed to deactivate school'); }
    }
  };

  const resetForm = () => {
    setShowForm(false);
    setEditingSchool(null);
    setFormData({ name: '', address: '', contact_email: '' });
  };

  const startEdit = (school) => {
    setEditingSchool(school);
    setFormData({ name: school.name, address: school.address || '', contact_email: school.contact_email || '' });
    setShowForm(true);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="font-heading text-3xl font-bold text-slate-900">Manage Schools</h1>
        <button onClick={() => setShowForm(true)} className="btn-primary flex items-center gap-2" data-testid="add-school"><Plus className="w-5 h-5" /> Add School</button>
      </div>

      {showForm && (
        <div className="card-plain">
          <h2 className="font-heading text-xl font-bold text-slate-900 mb-4">{editingSchool ? 'Edit School' : 'Add New School'}</h2>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block font-body font-semibold text-slate-700 mb-1">School Name *</label>
                <input type="text" value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} className="input-artsy" required data-testid="school-name" />
              </div>
              <div>
                <label className="block font-body font-semibold text-slate-700 mb-1">Contact Email</label>
                <input type="email" value={formData.contact_email} onChange={(e) => setFormData({...formData, contact_email: e.target.value})} className="input-artsy" />
              </div>
              <div className="md:col-span-2">
                <label className="block font-body font-semibold text-slate-700 mb-1">Address</label>
                <input type="text" value={formData.address} onChange={(e) => setFormData({...formData, address: e.target.value})} className="input-artsy" />
              </div>
            </div>
            <div className="flex gap-3">
              <button type="submit" className="btn-primary">{editingSchool ? 'Update' : 'Create'} School</button>
              <button type="button" onClick={resetForm} className="btn-outline">Cancel</button>
            </div>
          </form>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-12"><div className="w-12 h-12 border-4 border-teal-600 border-t-transparent rounded-full animate-spin"></div></div>
      ) : schools.length === 0 ? (
        <div className="card-plain text-center py-12"><School className="w-16 h-16 text-slate-300 mx-auto mb-4" /><p className="font-body text-slate-600">No schools yet. Create your first school!</p></div>
      ) : (
        <div className="space-y-4">
          {schools.map(school => (
            <div key={school.id} className="card-plain flex items-center justify-between">
              <div className="flex-1">
                <h3 className="font-heading text-lg font-bold text-slate-900">{school.name}</h3>
                <p className="font-body text-sm text-slate-600">{school.address}</p>
                {!school.is_active && <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded">Inactive</span>}
              </div>
              <div className="flex gap-2">
                <button onClick={() => startEdit(school)} className="p-2 hover:bg-slate-100 rounded-lg"><Edit className="w-5 h-5 text-slate-600" /></button>
                <button onClick={() => handleDelete(school.id)} className="p-2 hover:bg-red-50 rounded-lg"><Trash2 className="w-5 h-5 text-red-500" /></button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Classes Management
const ClassesPage = () => {
  const [classes, setClasses] = useState([]);
  const [schools, setSchools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingClass, setEditingClass] = useState(null);
  const [formData, setFormData] = useState({
    name: '', description: '', school_id: '', year_groups: [1,2,3,4,5,6], max_students: 20,
    num_weeks: 12, price_per_session: 8, sibling_price_per_session: 7,
    start_date: '', end_date: '', day_of_week: 'Wednesday', time: '3:30 PM - 5:00 PM', 
    is_active: true, is_hidden: false
  });

  const fetchData = useCallback(async () => {
    try {
      const [classesRes, schoolsRes] = await Promise.all([
        axios.get(`${API}/classes`),
        axios.get(`${API}/admin/schools`)
      ]);
      setClasses(classesRes.data);
      setSchools(schoolsRes.data.filter(s => s.is_active));
    } catch (error) { console.error('Failed to fetch data:', error); } 
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingClass) {
        await axios.put(`${API}/admin/classes/${editingClass.id}`, formData);
        toast.success('Class updated');
      } else {
        await axios.post(`${API}/admin/classes`, formData);
        toast.success('Class created');
      }
      fetchData();
      resetForm();
    } catch (error) { toast.error('Failed to save class'); }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to deactivate this class?')) {
      try {
        await axios.delete(`${API}/admin/classes/${id}`);
        toast.success('Class deactivated');
        fetchData();
      } catch (error) { toast.error('Failed to deactivate class'); }
    }
  };

  const resetForm = () => {
    setShowForm(false);
    setEditingClass(null);
    setFormData({
      name: '', description: '', school_id: '', year_groups: [1,2,3,4,5,6], max_students: 20,
      num_weeks: 12, price_per_session: 8, sibling_price_per_session: 7,
      start_date: '', end_date: '', day_of_week: 'Wednesday', time: '3:30 PM - 5:00 PM', 
      is_active: true, is_hidden: false
    });
  };

  const startEdit = (cls) => {
    setEditingClass(cls);
    setFormData({
      name: cls.name, description: cls.description, school_id: cls.school_id,
      year_groups: cls.year_groups || [1,2,3,4,5,6], max_students: cls.max_students,
      num_weeks: cls.num_weeks || 12, price_per_session: cls.price_per_session,
      sibling_price_per_session: cls.sibling_price_per_session,
      start_date: cls.start_date, end_date: cls.end_date || '',
      day_of_week: cls.day_of_week, time: cls.time, 
      is_active: cls.is_active, is_hidden: cls.is_hidden || false
    });
    setShowForm(true);
  };

  const totalPrice = formData.price_per_session * formData.num_weeks;
  const siblingTotal = formData.sibling_price_per_session * formData.num_weeks;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="font-heading text-3xl font-bold text-slate-900">Manage Classes</h1>
        <button onClick={() => setShowForm(true)} className="btn-primary flex items-center gap-2" data-testid="add-class"><Plus className="w-5 h-5" /> Add Class</button>
      </div>

      {showForm && (
        <div className="card-plain">
          <h2 className="font-heading text-xl font-bold text-slate-900 mb-4">{editingClass ? 'Edit Class' : 'Add New Class'}</h2>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block font-body font-semibold text-slate-700 mb-1">Class Name *</label>
                <input type="text" value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} className="input-artsy" required data-testid="class-name" />
              </div>
              <div>
                <label className="block font-body font-semibold text-slate-700 mb-1">School *</label>
                <select value={formData.school_id} onChange={(e) => setFormData({...formData, school_id: e.target.value})} className="input-artsy" required data-testid="class-school">
                  <option value="">Select school</option>
                  {schools.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </div>
              <div>
                <label className="block font-body font-semibold text-slate-700 mb-1">Start Date *</label>
                <input type="date" value={formData.start_date} onChange={(e) => setFormData({...formData, start_date: e.target.value})} className="input-artsy" required data-testid="class-date" />
              </div>
              <div>
                <label className="block font-body font-semibold text-slate-700 mb-1">End Date (optional)</label>
                <input type="date" value={formData.end_date} onChange={(e) => setFormData({...formData, end_date: e.target.value})} className="input-artsy" data-testid="class-end-date" />
                <p className="font-body text-xs text-slate-500 mt-1">Classes auto-hide after this date</p>
              </div>
              <div>
                <label className="block font-body font-semibold text-slate-700 mb-1">Day of Week *</label>
                <select value={formData.day_of_week} onChange={(e) => setFormData({...formData, day_of_week: e.target.value})} className="input-artsy" data-testid="class-day">
                  {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'].map(day => <option key={day} value={day}>{day}</option>)}
                </select>
              </div>
              <div>
                <label className="block font-body font-semibold text-slate-700 mb-1">Time *</label>
                <input type="text" value={formData.time} onChange={(e) => setFormData({...formData, time: e.target.value})} className="input-artsy" placeholder="3:30 PM - 5:00 PM" required data-testid="class-time" />
              </div>
              <div>
                <label className="block font-body font-semibold text-slate-700 mb-1">Number of Weeks *</label>
                <input type="number" value={formData.num_weeks} onChange={(e) => setFormData({...formData, num_weeks: parseInt(e.target.value)})} className="input-artsy" min="1" required data-testid="class-weeks" />
              </div>
              <div>
                <label className="block font-body font-semibold text-slate-700 mb-1">Max Students *</label>
                <input type="number" value={formData.max_students} onChange={(e) => setFormData({...formData, max_students: parseInt(e.target.value)})} className="input-artsy" min="1" required data-testid="class-max" />
              </div>
              <div>
                <label className="block font-body font-semibold text-slate-700 mb-1">Price per Session (£) *</label>
                <input type="number" value={formData.price_per_session} onChange={(e) => setFormData({...formData, price_per_session: parseFloat(e.target.value)})} className="input-artsy" step="0.01" required data-testid="class-price" />
              </div>
              <div>
                <label className="block font-body font-semibold text-slate-700 mb-1">Sibling Price (£) *</label>
                <input type="number" value={formData.sibling_price_per_session} onChange={(e) => setFormData({...formData, sibling_price_per_session: parseFloat(e.target.value)})} className="input-artsy" step="0.01" required data-testid="class-sibling-price" />
              </div>
            </div>
            
            {/* Visibility Toggle */}
            <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
              <div className="flex items-center justify-between">
                <div>
                  <label className="font-body font-semibold text-slate-700">Show on Public Site</label>
                  <p className="font-body text-sm text-slate-500">Toggle off to hide this class from the Art Club page</p>
                </div>
                <button
                  type="button"
                  onClick={() => setFormData({...formData, is_hidden: !formData.is_hidden})}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${!formData.is_hidden ? 'bg-teal-500' : 'bg-slate-300'}`}
                  data-testid="class-hidden-toggle"
                >
                  <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${!formData.is_hidden ? 'translate-x-6' : 'translate-x-1'}`} />
                </button>
              </div>
              {formData.is_hidden && (
                <div className="mt-2 p-2 bg-amber-50 rounded-lg border border-amber-200">
                  <p className="font-body text-sm text-amber-700 flex items-center gap-2">
                    <span className="font-semibold">🔒 Hidden</span> - This class won't be visible to parents on the website
                  </p>
                </div>
              )}
            </div>
            
            <div className="bg-slate-50 p-3 rounded-lg">
              <p className="font-body text-sm text-slate-600">Calculated prices: <strong>£{totalPrice.toFixed(2)}</strong> standard / <strong>£{siblingTotal.toFixed(2)}</strong> sibling</p>
            </div>
            <div>
              <label className="block font-body font-semibold text-slate-700 mb-1">Description *</label>
              <textarea value={formData.description} onChange={(e) => setFormData({...formData, description: e.target.value})} className="input-artsy" rows="3" required data-testid="class-description" />
            </div>
            <div className="flex gap-3">
              <button type="submit" className="btn-primary" data-testid="save-class">{editingClass ? 'Update' : 'Create'} Class</button>
              <button type="button" onClick={resetForm} className="btn-outline">Cancel</button>
            </div>
          </form>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-12"><div className="w-12 h-12 border-4 border-teal-600 border-t-transparent rounded-full animate-spin"></div></div>
      ) : classes.length === 0 ? (
        <div className="card-plain text-center py-12"><Calendar className="w-16 h-16 text-slate-300 mx-auto mb-4" /><p className="font-body text-slate-600">No classes yet. Create your first class!</p></div>
      ) : (
        <div className="space-y-4">
          {classes.map(cls => {
            const isHidden = cls.is_hidden;
            const hasEnded = cls.end_date && new Date(cls.end_date) < new Date();
            const isFull = cls.enrolled_count >= cls.max_students;
            
            return (
              <div key={cls.id} className={`card-plain flex items-center justify-between ${isHidden ? 'opacity-60 border-red-200' : ''}`} data-testid={`admin-class-${cls.id}`}>
                <div className="flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-heading text-lg font-bold text-slate-900">{cls.name}</h3>
                    {isHidden && (
                      <span className="bg-red-100 text-red-700 text-xs font-bold px-2 py-0.5 rounded-full">HIDDEN</span>
                    )}
                    {hasEnded && (
                      <span className="bg-slate-100 text-slate-600 text-xs font-bold px-2 py-0.5 rounded-full">ENDED</span>
                    )}
                    {isFull && !hasEnded && (
                      <span className="bg-yellow-100 text-yellow-700 text-xs font-bold px-2 py-0.5 rounded-full">FULL</span>
                    )}
                  </div>
                  <p className="font-body text-sm text-slate-600">{cls.day_of_week} • {cls.time} • {cls.school_name}</p>
                  <p className="font-body text-sm text-slate-500">{cls.enrolled_count}/{cls.max_students} enrolled • £{cls.total_price}/term</p>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => startEdit(cls)} className="p-2 hover:bg-slate-100 rounded-lg" title="Edit class"><Edit className="w-5 h-5 text-slate-600" /></button>
                  <button onClick={() => handleDelete(cls.id)} className="p-2 hover:bg-red-50 rounded-lg" title="Deactivate class"><Trash2 className="w-5 h-5 text-red-500" /></button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// Workshops Management
const WorkshopsPage = () => {
  const [workshops, setWorkshops] = useState([]);
  const [schools, setSchools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingWorkshop, setEditingWorkshop] = useState(null);
  const [formData, setFormData] = useState({ name: '', description: '', school_id: '', date: '', time: '', duration: '', price: 0, max_participants: 15, image_url: '', is_hidden: false });

  const fetchData = useCallback(async () => {
    try {
      const [workshopsRes, schoolsRes] = await Promise.all([
        axios.get(`${API}/admin/workshops`),
        axios.get(`${API}/admin/schools`)
      ]);
      setWorkshops(workshopsRes.data);
      setSchools(schoolsRes.data.filter(s => s.is_active));
    } catch (error) { console.error('Failed to fetch data:', error); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingWorkshop) {
        await axios.put(`${API}/admin/workshops/${editingWorkshop.id}`, formData);
        toast.success('Workshop updated');
      } else {
        await axios.post(`${API}/admin/workshops`, formData);
        toast.success('Workshop created');
      }
      fetchData();
      resetForm();
    } catch (error) { toast.error('Failed to save workshop'); }
  };

  const resetForm = () => {
    setShowForm(false);
    setEditingWorkshop(null);
    setFormData({ name: '', description: '', school_id: '', date: '', time: '', duration: '', price: 0, max_participants: 15, image_url: '', is_hidden: false });
  };

  const startEdit = (ws) => {
    setEditingWorkshop(ws);
    setFormData({ name: ws.name, description: ws.description, school_id: ws.school_id, date: ws.date, time: ws.time, duration: ws.duration, price: ws.price, max_participants: ws.max_participants, image_url: ws.image_url || '', is_hidden: ws.is_hidden || false });
    setShowForm(true);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="font-heading text-3xl font-bold text-slate-900">Manage Workshops</h1>
        <button onClick={() => setShowForm(true)} className="btn-secondary flex items-center gap-2" data-testid="add-workshop"><Plus className="w-5 h-5" /> Add Workshop</button>
      </div>

      {showForm && (
        <div className="card-plain">
          <h2 className="font-heading text-xl font-bold text-slate-900 mb-4">{editingWorkshop ? 'Edit Workshop' : 'Add New Workshop'}</h2>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div><label className="block font-body font-semibold text-slate-700 mb-1">Workshop Name *</label><input type="text" value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} className="input-artsy" required /></div>
              <div><label className="block font-body font-semibold text-slate-700 mb-1">School *</label><select value={formData.school_id} onChange={(e) => setFormData({...formData, school_id: e.target.value})} className="input-artsy" required><option value="">Select school</option>{schools.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}</select></div>
              <div><label className="block font-body font-semibold text-slate-700 mb-1">Date *</label><input type="date" value={formData.date} onChange={(e) => setFormData({...formData, date: e.target.value})} className="input-artsy" required /></div>
              <div><label className="block font-body font-semibold text-slate-700 mb-1">Time *</label><input type="text" value={formData.time} onChange={(e) => setFormData({...formData, time: e.target.value})} className="input-artsy" placeholder="10:00 AM - 3:00 PM" required /></div>
              <div><label className="block font-body font-semibold text-slate-700 mb-1">Duration *</label><input type="text" value={formData.duration} onChange={(e) => setFormData({...formData, duration: e.target.value})} className="input-artsy" placeholder="5 hours" required /></div>
              <div><label className="block font-body font-semibold text-slate-700 mb-1">Price (£) *</label><input type="number" value={formData.price} onChange={(e) => setFormData({...formData, price: parseFloat(e.target.value)})} className="input-artsy" step="0.01" required /></div>
              <div><label className="block font-body font-semibold text-slate-700 mb-1">Max Participants *</label><input type="number" value={formData.max_participants} onChange={(e) => setFormData({...formData, max_participants: parseInt(e.target.value)})} className="input-artsy" min="1" required /></div>
              <div><label className="block font-body font-semibold text-slate-700 mb-1">Image URL</label><input type="url" value={formData.image_url} onChange={(e) => setFormData({...formData, image_url: e.target.value})} className="input-artsy" placeholder="https://..." /></div>
            </div>
            <div><label className="block font-body font-semibold text-slate-700 mb-1">Description *</label><textarea value={formData.description} onChange={(e) => setFormData({...formData, description: e.target.value})} className="input-artsy" rows="3" required /></div>
            
            {/* Visibility Toggle */}
            <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
              <div className="flex items-center justify-between">
                <div>
                  <label className="font-body font-semibold text-slate-700">Show on Public Site</label>
                  <p className="font-body text-sm text-slate-500">Toggle off to hide this workshop from the Workshops page</p>
                </div>
                <button
                  type="button"
                  onClick={() => setFormData({...formData, is_hidden: !formData.is_hidden})}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${!formData.is_hidden ? 'bg-teal-500' : 'bg-slate-300'}`}
                  data-testid="workshop-hidden-toggle"
                >
                  <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${!formData.is_hidden ? 'translate-x-6' : 'translate-x-1'}`} />
                </button>
              </div>
              {formData.is_hidden && (
                <div className="mt-2 p-2 bg-amber-50 rounded-lg border border-amber-200">
                  <p className="font-body text-sm text-amber-700 flex items-center gap-2">
                    <span className="font-semibold">🔒 Hidden</span> - This workshop won't be visible to parents on the website
                  </p>
                </div>
              )}
            </div>
            
            <div className="flex gap-3"><button type="submit" className="btn-secondary">{editingWorkshop ? 'Update' : 'Create'} Workshop</button><button type="button" onClick={resetForm} className="btn-outline">Cancel</button></div>
          </form>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-12"><div className="w-12 h-12 border-4 border-pink-500 border-t-transparent rounded-full animate-spin"></div></div>
      ) : workshops.length === 0 ? (
        <div className="card-plain text-center py-12"><Calendar className="w-16 h-16 text-slate-300 mx-auto mb-4" /><p className="font-body text-slate-600">No workshops yet. Create your first workshop!</p></div>
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          {workshops.map(ws => (
            <div key={ws.id} className={`card-plain ${ws.is_hidden ? 'opacity-60 border-amber-200' : ''}`}>
              <div className="flex items-start justify-between mb-2">
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="font-heading text-lg font-bold text-slate-900">{ws.name}</h3>
                  {ws.is_hidden && (
                    <span className="bg-amber-100 text-amber-700 text-xs font-bold px-2 py-0.5 rounded-full">HIDDEN</span>
                  )}
                </div>
                <div className="flex gap-1"><button onClick={() => startEdit(ws)} className="p-2 hover:bg-slate-100 rounded-lg"><Edit className="w-4 h-4 text-slate-600" /></button></div>
              </div>
              <p className="font-body text-sm text-slate-600 mb-2">{ws.date} • {ws.time}</p>
              <p className="font-body text-sm text-slate-500">{ws.enrolled_count}/{ws.max_participants} enrolled • £{ws.price}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Weekly Summaries Management with Preview
const SummariesPage = () => {
  const [summaries, setSummaries] = useState([]);
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [editingSummary, setEditingSummary] = useState(null);
  const [formData, setFormData] = useState({ class_id: '', week_number: 1, title: '', content: '', photos: [] });

  const fetchData = useCallback(async () => {
    try {
      const [summariesRes, classesRes] = await Promise.all([axios.get(`${API}/admin/weekly-summaries`), axios.get(`${API}/classes`)]);
      setSummaries(summariesRes.data);
      setClasses(classesRes.data);
    } catch (error) { console.error('Failed to fetch data:', error); } 
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingSummary) {
        await axios.put(`${API}/admin/weekly-summaries/${editingSummary.id}`, formData);
        toast.success('Summary updated');
      } else {
        await axios.post(`${API}/admin/weekly-summaries`, formData);
        toast.success('Summary created');
      }
      fetchData();
      resetForm();
    } catch (error) { toast.error('Failed to save summary'); }
  };

  const handlePublish = async (id) => {
    try {
      await axios.post(`${API}/admin/weekly-summaries/${id}/publish`);
      toast.success('Summary published and emails sent!');
      fetchData();
    } catch (error) { toast.error('Failed to publish summary'); }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this summary?')) {
      try {
        await axios.delete(`${API}/admin/weekly-summaries/${id}`);
        toast.success('Summary deleted');
        fetchData();
      } catch (error) { toast.error('Failed to delete summary'); }
    }
  };

  const handlePreview = async (summary) => {
    try {
      const response = await axios.get(`${API}/admin/weekly-summaries/${summary.id}/preview`);
      setPreviewData(response.data);
      setShowPreview(true);
    } catch (error) { toast.error('Failed to load preview'); }
  };

  const resetForm = () => {
    setShowForm(false);
    setEditingSummary(null);
    setFormData({ class_id: '', week_number: 1, title: '', content: '', photos: [] });
  };

  const startEdit = (summary) => {
    setEditingSummary(summary);
    setFormData({ class_id: summary.class_id, week_number: summary.week_number, title: summary.title, content: summary.content, photos: summary.photos || [] });
    setShowForm(true);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="font-heading text-3xl font-bold text-slate-900">Weekly Summaries</h1>
        <button onClick={() => setShowForm(true)} className="btn-accent flex items-center gap-2" data-testid="add-summary"><Plus className="w-5 h-5" /> Add Summary</button>
      </div>

      {/* Preview Modal */}
      {showPreview && previewData && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[80vh] overflow-y-auto p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-heading text-xl font-bold text-slate-900">Parent Preview</h2>
              <button onClick={() => setShowPreview(false)} className="p-2 hover:bg-slate-100 rounded-lg">✕</button>
            </div>
            <div className="border-2 border-slate-200 rounded-xl p-6">
              <div className="border-l-4 border-teal-600 pl-4 mb-4">
                <span className="text-xs font-bold bg-yellow-100 text-yellow-700 px-2 py-1 rounded">Week {previewData.week_number}</span>
                <h3 className="font-heading text-2xl font-bold text-slate-900 mt-2">{previewData.title}</h3>
                <p className="font-body text-sm text-slate-500">{previewData.class_name}</p>
              </div>
              <div className="font-body text-slate-700 whitespace-pre-wrap">{previewData.content}</div>
              {previewData.photos && previewData.photos.length > 0 && (
                <div className="grid grid-cols-2 gap-2 mt-4">
                  {previewData.photos.map((photo, idx) => <img key={idx} src={photo} alt={`Week ${previewData.week_number}`} className="rounded-lg" />)}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {showForm && (
        <div className="card-plain">
          <h2 className="font-heading text-xl font-bold text-slate-900 mb-4">{editingSummary ? 'Edit Summary' : 'Create Weekly Summary'}</h2>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div><label className="block font-body font-semibold text-slate-700 mb-1">Class *</label><select value={formData.class_id} onChange={(e) => setFormData({...formData, class_id: e.target.value})} className="input-artsy" required><option value="">Select class</option>{classes.map(cls => <option key={cls.id} value={cls.id}>{cls.name}</option>)}</select></div>
              <div><label className="block font-body font-semibold text-slate-700 mb-1">Week Number *</label><input type="number" value={formData.week_number} onChange={(e) => setFormData({...formData, week_number: parseInt(e.target.value)})} className="input-artsy" min="1" max="52" required /></div>
            </div>
            <div><label className="block font-body font-semibold text-slate-700 mb-1">Title *</label><input type="text" value={formData.title} onChange={(e) => setFormData({...formData, title: e.target.value})} className="input-artsy" placeholder="e.g., Introduction to Watercolours" required /></div>
            <div><label className="block font-body font-semibold text-slate-700 mb-1">Content *</label><textarea value={formData.content} onChange={(e) => setFormData({...formData, content: e.target.value})} className="input-artsy" rows="6" placeholder="This week we learned..." required /></div>
            <div className="flex gap-3"><button type="submit" className="btn-accent">{editingSummary ? 'Update' : 'Save'} Summary</button><button type="button" onClick={resetForm} className="btn-outline">Cancel</button></div>
          </form>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-12"><div className="w-12 h-12 border-4 border-yellow-500 border-t-transparent rounded-full animate-spin"></div></div>
      ) : summaries.length === 0 ? (
        <div className="card-plain text-center py-12"><BookOpen className="w-16 h-16 text-slate-300 mx-auto mb-4" /><p className="font-body text-slate-600">No summaries yet. Create your first weekly summary!</p></div>
      ) : (
        <div className="space-y-4">
          {summaries.map(summary => (
            <div key={summary.id} className="card-plain flex items-center justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-xs font-bold bg-yellow-100 text-yellow-700 px-2 py-1 rounded">Week {summary.week_number}</span>
                  {summary.published ? <span className="text-xs font-bold bg-teal-100 text-teal-700 px-2 py-1 rounded">Published</span> : <span className="text-xs font-bold bg-slate-100 text-slate-700 px-2 py-1 rounded">Draft</span>}
                </div>
                <h3 className="font-heading text-lg font-bold text-slate-900">{summary.title}</h3>
                <p className="font-body text-sm text-slate-600">{summary.class_name}</p>
              </div>
              <div className="flex gap-2">
                <button onClick={() => handlePreview(summary)} className="p-2 hover:bg-slate-100 rounded-lg" title="Preview"><Eye className="w-5 h-5 text-slate-600" /></button>
                <button onClick={() => startEdit(summary)} className="p-2 hover:bg-slate-100 rounded-lg" title="Edit"><Edit className="w-5 h-5 text-slate-600" /></button>
                <button onClick={() => handleDelete(summary.id)} className="p-2 hover:bg-red-50 rounded-lg" title="Delete"><Trash2 className="w-5 h-5 text-red-500" /></button>
                {!summary.published && <button onClick={() => handlePublish(summary.id)} className="btn-primary text-sm py-2 px-4" data-testid={`publish-${summary.id}`}><Send className="w-4 h-4 inline mr-1" /> Publish</button>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Waiting List Management
const WaitingListPage = () => {
  const [waitingList, setWaitingList] = useState([]);
  const [children, setChildren] = useState([]);
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({ child_id: '', class_id: '' });

  const fetchData = useCallback(async () => {
    try {
      const [waitingRes, childrenRes, classesRes] = await Promise.all([
        axios.get(`${API}/admin/waiting-list`),
        axios.get(`${API}/admin/children`),
        axios.get(`${API}/classes`)
      ]);
      setWaitingList(waitingRes.data);
      setChildren(childrenRes.data);
      setClasses(classesRes.data);
    } catch (error) { console.error('Failed to fetch data:', error); } 
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/admin/waiting-list`, formData);
      toast.success('Added to waiting list');
      fetchData();
      resetForm();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to add to waiting list'); }
  };

  const resetForm = () => {
    setShowForm(false);
    setFormData({ child_id: '', class_id: '' });
  };

  const handleNotify = async (id) => {
    try {
      await axios.post(`${API}/admin/waiting-list/${id}/move-to-class`);
      toast.success('Parent notified about available space');
      fetchData();
    } catch (error) { toast.error('Failed to notify parent'); }
  };

  const handleRemove = async (id) => {
    if (window.confirm('Remove from waiting list?')) {
      try {
        await axios.delete(`${API}/admin/waiting-list/${id}`);
        toast.success('Removed from waiting list');
        fetchData();
      } catch (error) { toast.error('Failed to remove'); }
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="font-heading text-3xl font-bold text-slate-900">Waiting List</h1>
        <button onClick={() => setShowForm(true)} className="btn-primary flex items-center gap-2" data-testid="add-to-waitlist"><Plus className="w-5 h-5" /> Add to Waiting List</button>
      </div>

      {showForm && (
        <div className="card-plain">
          <h2 className="font-heading text-xl font-bold text-slate-900 mb-4">Add Child to Waiting List</h2>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block font-body font-semibold text-slate-700 mb-1">Select Child *</label>
                <select value={formData.child_id} onChange={(e) => setFormData({...formData, child_id: e.target.value})} className="input-artsy" required>
                  <option value="">Choose a child</option>
                  {children.map(child => <option key={child.id} value={child.id}>{child.name} ({child.parent_email})</option>)}
                </select>
              </div>
              <div>
                <label className="block font-body font-semibold text-slate-700 mb-1">Select Class *</label>
                <select value={formData.class_id} onChange={(e) => setFormData({...formData, class_id: e.target.value})} className="input-artsy" required>
                  <option value="">Choose a class</option>
                  {classes.map(cls => <option key={cls.id} value={cls.id}>{cls.name} - {cls.day_of_week}s</option>)}
                </select>
              </div>
            </div>
            <div className="flex gap-3">
              <button type="submit" className="btn-primary">Add to Waiting List</button>
              <button type="button" onClick={resetForm} className="btn-outline">Cancel</button>
            </div>
          </form>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-12"><div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div></div>
      ) : waitingList.length === 0 ? (
        <div className="card-plain text-center py-12"><Clock className="w-16 h-16 text-slate-300 mx-auto mb-4" /><p className="font-body text-slate-600">No one on the waiting list.</p></div>
      ) : (
        <div className="space-y-4">
          {waitingList.map(entry => (
            <div key={entry.id} className="card-plain flex items-center justify-between">
              <div><h3 className="font-heading font-bold text-slate-900">{entry.child_name}</h3><p className="font-body text-sm text-slate-600">{entry.class_name}</p><p className="font-body text-xs text-slate-500">Parent: {entry.parent_email} • Added: {new Date(entry.created_at).toLocaleDateString()}</p></div>
              <div className="flex items-center gap-2">
                {entry.notified && <span className="text-xs font-bold bg-teal-100 text-teal-700 px-2 py-1 rounded">Notified</span>}
                <button onClick={() => handleNotify(entry.id)} className="btn-primary text-sm py-2 px-4" disabled={entry.notified}><Mail className="w-4 h-4 inline mr-1" /> Notify</button>
                <button onClick={() => handleRemove(entry.id)} className="p-2 hover:bg-red-50 rounded-lg" title="Remove"><Trash2 className="w-5 h-5 text-red-500" /></button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Payments Management
const PaymentsPage = () => {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  const fetchPayments = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/payments`);
      setPayments(response.data);
    } catch (error) { console.error('Failed to fetch payments:', error); } 
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchPayments(); }, [fetchPayments]);

  const handleRefund = async (id) => {
    if (window.confirm('Are you sure you want to mark this payment as refunded?')) {
      try {
        await axios.post(`${API}/admin/payments/${id}/refund`);
        toast.success('Payment marked as refunded');
        fetchPayments();
      } catch (error) { toast.error('Failed to process refund'); }
    }
  };

  const filteredPayments = payments.filter(p => {
    if (filter === 'all') return true;
    return p.payment_status === filter;
  });

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <h1 className="font-heading text-3xl font-bold text-slate-900">Payments</h1>
        <div className="flex gap-2">
          {['all', 'paid', 'pending', 'refunded'].map(status => (
            <button
              key={status}
              onClick={() => setFilter(status)}
              className={`px-3 py-1 text-sm font-body font-semibold rounded-lg transition-colors ${filter === status ? 'bg-teal-600 text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}`}
              data-testid={`filter-${status}`}
            >
              {status.charAt(0).toUpperCase() + status.slice(1)}
            </button>
          ))}
        </div>
      </div>
      {loading ? (
        <div className="flex justify-center py-12"><div className="w-12 h-12 border-4 border-teal-600 border-t-transparent rounded-full animate-spin"></div></div>
      ) : filteredPayments.length === 0 ? (
        <div className="card-plain text-center py-12"><CreditCard className="w-16 h-16 text-slate-300 mx-auto mb-4" /><p className="font-body text-slate-600">No payments found.</p></div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="payments-table">
            <thead>
              <tr className="border-b border-slate-200">
                <th className="text-left py-3 px-4 font-heading font-bold text-slate-900">Date</th>
                <th className="text-left py-3 px-4 font-heading font-bold text-slate-900">Parent</th>
                <th className="text-left py-3 px-4 font-heading font-bold text-slate-900">Children</th>
                <th className="text-left py-3 px-4 font-heading font-bold text-slate-900">Type</th>
                <th className="text-left py-3 px-4 font-heading font-bold text-slate-900">Materials</th>
                <th className="text-left py-3 px-4 font-heading font-bold text-slate-900">Amount</th>
                <th className="text-left py-3 px-4 font-heading font-bold text-slate-900">Status</th>
                <th className="text-left py-3 px-4 font-heading font-bold text-slate-900">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredPayments.map(payment => (
                <tr key={payment.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`payment-row-${payment.id}`}>
                  <td className="py-3 px-4 font-body text-slate-700">{new Date(payment.created_at).toLocaleDateString()}</td>
                  <td className="py-3 px-4">
                    <div className="font-body text-slate-900 font-semibold">{payment.parent_name || 'Unknown'}</div>
                    <div className="font-body text-slate-500 text-sm">{payment.user_email}</div>
                  </td>
                  <td className="py-3 px-4 font-body text-slate-700">
                    {payment.child_names && payment.child_names.length > 0 
                      ? payment.child_names.join(', ') 
                      : <span className="text-slate-400">N/A</span>}
                  </td>
                  <td className="py-3 px-4">
                    <span className={`text-xs font-bold px-2 py-1 rounded ${
                      payment.enrollment_type_label === 'Class' ? 'bg-blue-100 text-blue-700' :
                      payment.enrollment_type_label === 'Workshop' ? 'bg-purple-100 text-purple-700' :
                      payment.enrollment_type_label === 'Shop' ? 'bg-orange-100 text-orange-700' :
                      'bg-slate-100 text-slate-700'
                    }`}>
                      {payment.enrollment_type_label || 'Unknown'}
                    </span>
                    <div className="font-body text-slate-600 text-sm mt-1">{payment.enrollment_name}</div>
                  </td>
                  <td className="py-3 px-4 font-body text-slate-700">
                    {payment.materials && payment.materials.length > 0 
                      ? <ul className="text-sm">{payment.materials.map((m, i) => <li key={i}>{m}</li>)}</ul>
                      : <span className="text-slate-400">-</span>}
                  </td>
                  <td className="py-3 px-4 font-heading font-bold text-slate-900">£{payment.amount.toFixed(2)}</td>
                  <td className="py-3 px-4">
                    <span className={`text-xs font-bold px-2 py-1 rounded ${
                      payment.payment_status === 'paid' ? 'bg-teal-100 text-teal-700' : 
                      payment.payment_status === 'refunded' ? 'bg-red-100 text-red-700' : 
                      'bg-yellow-100 text-yellow-700'
                    }`}>
                      {payment.payment_status}
                    </span>
                  </td>
                  <td className="py-3 px-4">
                    {payment.payment_status === 'paid' && (
                      <button 
                        onClick={() => handleRefund(payment.id)} 
                        className="text-sm text-red-500 hover:text-red-700 font-body font-semibold"
                        data-testid={`refund-${payment.id}`}
                      >
                        Refund
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};


// Shop Items Management
const ShopItemsPage = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({ name: '', description: '', price: 0, category: '', image_url: '', stock: 100, is_starter_pack_item: false });

  useEffect(() => { fetchItems(); }, []);

  const fetchItems = async () => {
    try {
      const response = await axios.get(`${API}/shop/items`);
      setItems(response.data);
    } catch (error) { console.error('Failed to fetch items:', error); } 
    finally { setLoading(false); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingItem) {
        await axios.put(`${API}/admin/shop/items/${editingItem.id}`, formData);
        toast.success('Item updated');
      } else {
        await axios.post(`${API}/admin/shop/items`, formData);
        toast.success('Item created');
      }
      fetchItems();
      resetForm();
    } catch (error) { toast.error('Failed to save item'); }
  };

  const resetForm = () => {
    setShowForm(false);
    setEditingItem(null);
    setFormData({ name: '', description: '', price: 0, category: '', image_url: '', stock: 100, is_starter_pack_item: false });
  };

  const startEdit = (item) => {
    setEditingItem(item);
    setFormData({ name: item.name, description: item.description, price: item.price, category: item.category, image_url: item.image_url, stock: item.stock, is_starter_pack_item: item.is_starter_pack_item || false });
    setShowForm(true);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="font-heading text-3xl font-bold text-slate-900">Shop Items</h1>
        <button onClick={() => setShowForm(true)} className="btn-primary flex items-center gap-2"><Plus className="w-5 h-5" /> Add Item</button>
      </div>

      {showForm && (
        <div className="card-plain">
          <h2 className="font-heading text-xl font-bold text-slate-900 mb-4">{editingItem ? 'Edit Item' : 'Add New Item'}</h2>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div><label className="block font-body font-semibold text-slate-700 mb-1">Name *</label><input type="text" value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} className="input-artsy" required /></div>
              <div><label className="block font-body font-semibold text-slate-700 mb-1">Category *</label><input type="text" value={formData.category} onChange={(e) => setFormData({...formData, category: e.target.value})} className="input-artsy" placeholder="e.g., Paper, Pencils, Paints" required /></div>
              <div><label className="block font-body font-semibold text-slate-700 mb-1">Price (£) *</label><input type="number" value={formData.price} onChange={(e) => setFormData({...formData, price: parseFloat(e.target.value)})} className="input-artsy" step="0.01" required /></div>
              <div><label className="block font-body font-semibold text-slate-700 mb-1">Stock *</label><input type="number" value={formData.stock} onChange={(e) => setFormData({...formData, stock: parseInt(e.target.value)})} className="input-artsy" required /></div>
              <div className="md:col-span-2"><label className="block font-body font-semibold text-slate-700 mb-1">Image URL *</label><input type="url" value={formData.image_url} onChange={(e) => setFormData({...formData, image_url: e.target.value})} className="input-artsy" required /></div>
            </div>
            <div><label className="block font-body font-semibold text-slate-700 mb-1">Description *</label><textarea value={formData.description} onChange={(e) => setFormData({...formData, description: e.target.value})} className="input-artsy" rows="2" required /></div>
            <div className="flex gap-3"><button type="submit" className="btn-primary">{editingItem ? 'Update' : 'Create'} Item</button><button type="button" onClick={resetForm} className="btn-outline">Cancel</button></div>
          </form>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-12"><div className="w-12 h-12 border-4 border-teal-600 border-t-transparent rounded-full animate-spin"></div></div>
      ) : (
        <div className="grid md:grid-cols-3 gap-4">
          {items.map(item => (
            <div key={item.id} className="card-plain">
              <img src={item.image_url} alt={item.name} className="w-full h-32 object-cover rounded-lg mb-3" />
              <h3 className="font-heading font-bold text-slate-900">{item.name}</h3>
              <p className="font-body text-sm text-slate-600">{item.category}</p>
              <div className="flex items-center justify-between mt-2">
                <span className="font-heading font-bold text-teal-600">£{item.price.toFixed(2)}</span>
                <button onClick={() => startEdit(item)} className="p-2 hover:bg-slate-100 rounded-lg"><Edit className="w-4 h-4 text-slate-600" /></button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Starter Pack Management
const StarterPackPage = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({ name: '', description: '', price: 0, image_url: '', is_included: true });

  useEffect(() => { fetchItems(); }, []);

  const fetchItems = async () => {
    try {
      const response = await axios.get(`${API}/admin/starter-pack`);
      setItems(response.data);
    } catch (error) { console.error('Failed to fetch items:', error); } 
    finally { setLoading(false); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingItem) {
        await axios.put(`${API}/admin/starter-pack/${editingItem.id}`, formData);
        toast.success('Item updated');
      } else {
        await axios.post(`${API}/admin/starter-pack`, formData);
        toast.success('Item added');
      }
      fetchItems();
      resetForm();
    } catch (error) { toast.error('Failed to save item'); }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Remove this item from starter pack?')) {
      try {
        await axios.delete(`${API}/admin/starter-pack/${id}`);
        toast.success('Item removed');
        fetchItems();
      } catch (error) { toast.error('Failed to remove item'); }
    }
  };

  const resetForm = () => {
    setShowForm(false);
    setEditingItem(null);
    setFormData({ name: '', description: '', price: 0, image_url: '', is_included: true });
  };

  const startEdit = (item) => {
    setEditingItem(item);
    setFormData({ name: item.name, description: item.description, price: item.price, image_url: item.image_url || '', is_included: item.is_included });
    setShowForm(true);
  };

  const totalValue = items.filter(i => i.is_included).reduce((sum, item) => sum + item.price, 0);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold text-slate-900">Starter Pack</h1>
          <p className="font-body text-slate-600">Total value: <strong>£{totalValue.toFixed(2)}</strong></p>
        </div>
        <button onClick={() => setShowForm(true)} className="btn-primary flex items-center gap-2"><Plus className="w-5 h-5" /> Add Item</button>
      </div>

      {showForm && (
        <div className="card-plain">
          <h2 className="font-heading text-xl font-bold text-slate-900 mb-4">{editingItem ? 'Edit Item' : 'Add New Item'}</h2>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div><label className="block font-body font-semibold text-slate-700 mb-1">Item Name *</label><input type="text" value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} className="input-artsy" required /></div>
              <div><label className="block font-body font-semibold text-slate-700 mb-1">Cost Price (£) *</label><input type="number" value={formData.price} onChange={(e) => setFormData({...formData, price: parseFloat(e.target.value)})} className="input-artsy" step="0.01" required /></div>
              <div className="md:col-span-2"><label className="block font-body font-semibold text-slate-700 mb-1">Description</label><input type="text" value={formData.description} onChange={(e) => setFormData({...formData, description: e.target.value})} className="input-artsy" /></div>
            </div>
            <label className="flex items-center gap-2 cursor-pointer"><input type="checkbox" checked={formData.is_included} onChange={(e) => setFormData({...formData, is_included: e.target.checked})} className="w-5 h-5 rounded" /><span className="font-body text-slate-700">Include in starter pack</span></label>
            <div className="flex gap-3"><button type="submit" className="btn-primary">{editingItem ? 'Update' : 'Add'} Item</button><button type="button" onClick={resetForm} className="btn-outline">Cancel</button></div>
          </form>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-12"><div className="w-12 h-12 border-4 border-teal-600 border-t-transparent rounded-full animate-spin"></div></div>
      ) : (
        <div className="space-y-2">
          {items.map(item => (
            <div key={item.id} className={`card-plain flex items-center justify-between ${!item.is_included ? 'opacity-50' : ''}`}>
              <div className="flex-1">
                <h3 className="font-heading font-bold text-slate-900">{item.name}</h3>
                <p className="font-body text-sm text-slate-600">{item.description}</p>
              </div>
              <div className="flex items-center gap-4">
                <span className="font-heading font-bold text-teal-600">£{item.price.toFixed(2)}</span>
                {!item.is_included && <span className="text-xs bg-slate-100 text-slate-500 px-2 py-1 rounded">Not included</span>}
                <button onClick={() => startEdit(item)} className="p-2 hover:bg-slate-100 rounded-lg"><Edit className="w-4 h-4 text-slate-600" /></button>
                <button onClick={() => handleDelete(item.id)} className="p-2 hover:bg-red-50 rounded-lg"><Trash2 className="w-4 h-4 text-red-500" /></button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Site Settings Management
const SiteSettingsPage = () => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const fetchSettings = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/site-settings`);
      setSettings(response.data);
    } catch (error) { console.error('Failed to fetch settings:', error); } 
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchSettings(); }, [fetchSettings]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.put(`${API}/admin/site-settings`, settings);
      toast.success('Settings saved');
    } catch (error) { toast.error('Failed to save settings'); } 
    finally { setSaving(false); }
  };

  if (loading) {
    return <div className="flex justify-center py-12"><div className="w-12 h-12 border-4 border-teal-600 border-t-transparent rounded-full animate-spin"></div></div>;
  }

  return (
    <div className="space-y-6">
      <h1 className="font-heading text-3xl font-bold text-slate-900">Site Settings</h1>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Contact Information */}
        <div className="card-plain">
          <h2 className="font-heading text-xl font-bold text-slate-900 mb-4">Contact Information</h2>
          <div className="grid md:grid-cols-2 gap-4">
            <div><label className="block font-body font-semibold text-slate-700 mb-1">Email</label><input type="email" value={settings?.contact_email || ''} onChange={(e) => setSettings({...settings, contact_email: e.target.value})} className="input-artsy" /></div>
            <div><label className="block font-body font-semibold text-slate-700 mb-1">Phone</label><input type="tel" value={settings?.contact_phone || ''} onChange={(e) => setSettings({...settings, contact_phone: e.target.value})} className="input-artsy" /></div>
            <div className="md:col-span-2"><label className="block font-body font-semibold text-slate-700 mb-1">Address</label><input type="text" value={settings?.contact_address || ''} onChange={(e) => setSettings({...settings, contact_address: e.target.value})} className="input-artsy" /></div>
          </div>
        </div>

        {/* Meet Your Art Teacher */}
        <div className="card-plain">
          <h2 className="font-heading text-xl font-bold text-slate-900 mb-4">Meet Your Art Teacher</h2>
          <div className="grid md:grid-cols-2 gap-4">
            <div><label className="block font-body font-semibold text-slate-700 mb-1">Teacher Name</label><input type="text" value={settings?.teacher_name || ''} onChange={(e) => setSettings({...settings, teacher_name: e.target.value})} className="input-artsy" /></div>
            <div><label className="block font-body font-semibold text-slate-700 mb-1">Teacher Image URL</label><input type="url" value={settings?.teacher_image_url || ''} onChange={(e) => setSettings({...settings, teacher_image_url: e.target.value})} className="input-artsy" /></div>
            <div className="md:col-span-2"><label className="block font-body font-semibold text-slate-700 mb-1">Teacher Bio</label><textarea value={settings?.teacher_bio || ''} onChange={(e) => setSettings({...settings, teacher_bio: e.target.value})} className="input-artsy" rows="3" /></div>
          </div>
        </div>

        {/* Social Media */}
        <div className="card-plain">
          <h2 className="font-heading text-xl font-bold text-slate-900 mb-4">Social Media</h2>
          <div className="grid md:grid-cols-3 gap-4">
            <div><label className="block font-body font-semibold text-slate-700 mb-1">Instagram URL</label><input type="url" value={settings?.instagram_url || ''} onChange={(e) => setSettings({...settings, instagram_url: e.target.value})} className="input-artsy" placeholder="https://instagram.com/..." /></div>
            <div><label className="block font-body font-semibold text-slate-700 mb-1">Facebook URL</label><input type="url" value={settings?.facebook_url || ''} onChange={(e) => setSettings({...settings, facebook_url: e.target.value})} className="input-artsy" placeholder="https://facebook.com/..." /></div>
            <div><label className="block font-body font-semibold text-slate-700 mb-1">Twitter URL</label><input type="url" value={settings?.twitter_url || ''} onChange={(e) => setSettings({...settings, twitter_url: e.target.value})} className="input-artsy" placeholder="https://twitter.com/..." /></div>
          </div>
        </div>

        <button type="submit" className="btn-primary" disabled={saving}>{saving ? 'Saving...' : 'Save Settings'}</button>
      </form>
    </div>
  );
};

// Main Admin Dashboard Component
export default function AdminDashboard() {
  const location = useLocation();

  const navItems = [
    { path: '/admin', icon: <Home className="w-5 h-5" />, label: 'Dashboard', exact: true },
    { path: '/admin/schools', icon: <School className="w-5 h-5" />, label: 'Schools' },
    { path: '/admin/classes', icon: <Calendar className="w-5 h-5" />, label: 'Classes' },
    { path: '/admin/workshops', icon: <Calendar className="w-5 h-5" />, label: 'Workshops' },
    { path: '/admin/summaries', icon: <BookOpen className="w-5 h-5" />, label: 'Summaries' },
    { path: '/admin/waiting-list', icon: <Clock className="w-5 h-5" />, label: 'Waiting List' },
    { path: '/admin/payments', icon: <CreditCard className="w-5 h-5" />, label: 'Payments' },
    { path: '/admin/shop', icon: <ShoppingBag className="w-5 h-5" />, label: 'Shop Items' },
    { path: '/admin/starter-pack', icon: <Package className="w-5 h-5" />, label: 'Starter Pack' },
    { path: '/admin/settings', icon: <Settings className="w-5 h-5" />, label: 'Site Settings' },
  ];

  const isActive = (path, exact = false) => {
    if (exact) return location.pathname === path;
    return location.pathname.startsWith(path) && (exact || path !== '/admin');
  };

  return (
    <div className="min-h-screen bg-artsy-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-col lg:flex-row gap-8">
          <div className="lg:w-64 flex-shrink-0">
            <div className="card-plain sticky top-24">
              <h2 className="font-heading text-lg font-bold text-slate-900 mb-4">Admin Panel</h2>
              <nav className="space-y-1">
                {navItems.map(item => (
                  <Link key={item.path} to={item.path} className={`flex items-center gap-3 px-4 py-3 rounded-xl font-body font-semibold transition-colors ${isActive(item.path, item.exact) ? 'bg-teal-600 text-white' : 'text-slate-700 hover:bg-slate-100'}`}>
                    {item.icon}{item.label}
                  </Link>
                ))}
              </nav>
              
              {/* Parent Preview Mode Button */}
              <div className="mt-6 pt-4 border-t border-slate-200">
                <Link 
                  to="/admin-preview" 
                  className="flex items-center gap-3 px-4 py-3 rounded-xl font-body font-semibold text-purple-600 hover:bg-purple-50 transition-colors"
                  data-testid="view-parent-dashboard"
                >
                  <Eye className="w-5 h-5" />
                  View Parent Dashboard
                  <ExternalLink className="w-4 h-4 ml-auto opacity-50" />
                </Link>
              </div>
            </div>
          </div>
          <div className="flex-1">
            <Routes>
              <Route index element={<AdminOverview />} />
              <Route path="schools" element={<SchoolsPage />} />
              <Route path="classes" element={<ClassesPage />} />
              <Route path="workshops" element={<WorkshopsPage />} />
              <Route path="summaries" element={<SummariesPage />} />
              <Route path="waiting-list" element={<WaitingListPage />} />
              <Route path="payments" element={<PaymentsPage />} />
              <Route path="shop" element={<ShopItemsPage />} />
              <Route path="starter-pack" element={<StarterPackPage />} />
              <Route path="settings" element={<SiteSettingsPage />} />
            </Routes>
          </div>
        </div>
      </div>
    </div>
  );
}
